/**
 * Criar mesa (CreateRoomPopup): nome (mínimo de 5 letras), sistema, complemento, língua (começa na da
 * interface), frequência, características, senha opcional e boas-vindas. Criar já entra na mesa
 * (Room_CreateAndJoin no original).
 */
import { useState } from 'react';
import { useLanguage, useT } from '../i18n/index.js';
import { ROOM_NAME_MIN, type RoomDetails } from '../shared.js';
import { useApp } from '../store.js';
import { Button, CheckBox, ComboBox, ErrorLabel, TextEdit } from '../ui/controls.js';
import { frequencyOptions, languageOptions, roomLanguageOf } from './roomDetails.js';
import { TagSelector } from './TagSelector.js';

export function CreateRoomPopup({ onClose }: { onClose: () => void }) {
  const t = useT();
  const ui = useLanguage((s) => s.language);
  const createRoom = useApp((s) => s.createRoom);
  const busy = useApp((s) => s.busy);
  const [name, setName] = useState('');
  const [system, setSystem] = useState('');
  const [complement, setComplement] = useState('');
  const [language, setLanguage] = useState<RoomDetails['language']>(() => roomLanguageOf(ui));
  const [frequency, setFrequency] = useState<RoomDetails['frequency']>('');
  const [tags, setTags] = useState<string[]>([]);
  const [withPassword, setWithPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [welcome, setWelcome] = useState('');
  const [hidden, setHidden] = useState(false);
  const [nameError, setNameError] = useState('');

  async function submit() {
    if (name.trim().length < ROOM_NAME_MIN) {
      setNameError(t('create.name.tooShort', { n: ROOM_NAME_MIN }));
      return;
    }
    await createRoom({
      name: name.trim(),
      rpgSystem: system.trim(),
      rpgSystemComplement: complement.trim(),
      tags,
      welcomeMessage: welcome.trim(),
      language,
      frequency,
      isPrivate: hidden,
      password: withPassword && password ? password : null,
    });
    if (useApp.getState().activeRoomId) onClose();
  }

  return (
    <div className="rd-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <form
        className="rd-dialog rd-create"
        role="dialog"
        aria-modal="true"
        aria-label={t('create.title')}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h2 className="rd-dialog__title">{t('create.title')}</h2>
        <div className="rd-create__grid">
          <label className="rd-field rd-create__wide">
            <span className="rd-field__label">{t('create.name')}</span>
            <TextEdit
              look="filled"
              value={name}
              onChange={(v) => {
                setName(v);
                if (nameError) setNameError('');
              }}
              placeholder={t('create.name.prompt')}
              maxLength={60}
              invalid={!!nameError}
              autoFocusOnMount
            />
            <ErrorLabel>{nameError}</ErrorLabel>
          </label>
          <label className="rd-field">
            <span className="rd-field__label">{t('create.system')}</span>
            <TextEdit look="filled" value={system} onChange={setSystem} placeholder={t('create.system.prompt')} maxLength={60} />
          </label>
          <label className="rd-field">
            <span className="rd-field__label">{t('create.complement')}</span>
            <TextEdit look="filled" value={complement} onChange={setComplement} placeholder={t('create.complement.prompt')} maxLength={60} />
          </label>
          <label className="rd-field">
            <span className="rd-field__label">{t('roomDetails.language')}</span>
            <ComboBox value={language} onChange={setLanguage} options={languageOptions(t, ui)} />
          </label>
          <label className="rd-field">
            <span className="rd-field__label">{t('roomDetails.frequency')}</span>
            <ComboBox value={frequency} onChange={setFrequency} options={frequencyOptions(t)} />
          </label>
          <div className="rd-field rd-create__wide">
            <span className="rd-field__label">{t('create.tags')}</span>
            <TagSelector value={tags} onChange={setTags} max={12} />
          </div>
          <label className="rd-field rd-create__wide">
            <span className="rd-field__label">{t('create.welcome')}</span>
            <textarea className="rd-edit rd-edit--filled rd-create__welcome" value={welcome} maxLength={500} rows={3} placeholder={t('create.welcome.prompt')} onChange={(e) => setWelcome(e.target.value)} />
          </label>
          <div className="rd-field">
            <CheckBox checked={withPassword} onChange={setWithPassword}>
              {t('create.password.toggle')}
            </CheckBox>
            {withPassword ? <TextEdit look="filled" icon="lock" type="password" value={password} onChange={setPassword} placeholder={t('create.password')} maxLength={120} /> : null}
          </div>
          <div className="rd-field">
            <CheckBox checked={hidden} onChange={setHidden}>
              {t('create.private')}
            </CheckBox>
          </div>
        </div>
        <div className="rd-dialog__actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button type="submit" variant="action" busy={busy} disabled={withPassword && !password}>
            {t('create.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
}
